import { useCallback, useEffect, useRef, useState } from 'react';
import type { WitcFinishAudit } from '../services/api';
import { decryptWitcBootstrapCipher } from '../utils/witcBootstrapDecrypt';
import {
  loadWitcAssemblyExports,
  type WitcWasmExports,
} from '../utils/witcWasmExports';

export interface WitcSubmissionResultDto {
  isCorrect: boolean;
  errorCountAfter: number;
  pointsEarned: number;
  isExhausted: boolean;
  revealedAnswer?: string | null;
  mediumHintLetters?: Array<{ index: number; char: string }> | null;
  isSessionComplete: boolean;
}

export interface WitcQuestionStateDto {
  questionId: string;
  errorCount: number;
  isExhausted: boolean;
  isCorrect: boolean;
  pointsEarned: number;
  mediumHintLetters: Array<{ index: number; char: string }>;
}

export interface WitcPlayStateDto {
  playSessionId: string;
  quizId: string;
  playDifficulty: string;
  activeQuestionIds: string[];
  currentQuestionIndex: number;
  totalScore: number;
  pointsPerQuestion: number[];
  questionStates: Record<string, WitcQuestionStateDto>;
  isSessionComplete: boolean;
}

const WASM_BASE =
  (process.env.VITE_WITC_WASM_BASE ?? '/wasm/witc').replace(/\/$/, '');

let dotnetModulePromise: Promise<{
  getAssemblyExports: (name: string) => Promise<Record<string, unknown>>;
  getConfig: () => { mainAssemblyName: string };
}> | null = null;

async function loadWasmExports(): Promise<WitcWasmExports> {
  if (!dotnetModulePromise) {
    dotnetModulePromise = (async () => {
      const dotnetUrl = `${WASM_BASE}/_framework/dotnet.js`;
      const bootConfigUrl = `${WASM_BASE}/_framework/blazor.boot.json`;
      const mod = await import(/* webpackIgnore: true */ dotnetUrl);
      const dotnet = mod.dotnet as
        | {
            withConfigSrc: (src: string) => { create: () => Promise<unknown> };
            create: () => Promise<unknown>;
          }
        | undefined;
      if (!dotnet?.create) {
        throw new Error('WITC WASM: dotnet runtime not found.');
      }
      const runtime = dotnet.withConfigSrc
        ? await dotnet.withConfigSrc(bootConfigUrl).create()
        : await dotnet.create();
      return runtime as {
        getAssemblyExports: (name: string) => Promise<Record<string, unknown>>;
        getConfig: () => { mainAssemblyName: string };
      };
    })();
  }

  const runtime = await dotnetModulePromise;
  const config = runtime.getConfig();
  return loadWitcAssemblyExports(runtime.getAssemblyExports, config.mainAssemblyName);
}

export function useWitcWasm(engineBootstrapCipher: string | undefined) {
  const exportsRef = useRef<WitcWasmExports | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!engineBootstrapCipher?.trim()) {
      setReady(false);
      setError(null);
      return;
    }

    setReady(false);
    setError(null);

    (async () => {
      try {
        const wasm = await loadWasmExports();
        if (cancelled) return;
        const keyB64 = wasm.getBootstrapKeyBase64();
        const plainB64 = await decryptWitcBootstrapCipher(engineBootstrapCipher, keyB64);
        if (cancelled) return;
        const initResult = wasm.initializeSessionFromPlaintext(plainB64);
        const initError =
          typeof initResult === 'string' ? initResult.trim() : 'Failed to initialize WITC engine.';
        if (initError) {
          setError(initError);
          setReady(false);
          return;
        }
        exportsRef.current = wasm;
        setError(null);
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'WITC WASM load failed.');
        setReady(false);
      }
    })();

    return () => {
      cancelled = true;
      exportsRef.current?.disposeSession();
      exportsRef.current = null;
      setReady(false);
    };
  }, [engineBootstrapCipher]);

  const submitAnswer = useCallback(
    async (questionId: string, text: string, userLanguage?: string): Promise<WitcSubmissionResultDto | null> => {
      const wasm = exportsRef.current;
      if (!wasm) return null;
      const json = wasm.submitAnswer(questionId, text, userLanguage);
      return JSON.parse(json) as WitcSubmissionResultDto;
    },
    []
  );

  const getPlayState = useCallback(async (): Promise<WitcPlayStateDto | null> => {
    const wasm = exportsRef.current;
    if (!wasm) return null;
    const json = wasm.getPlayState();
    return JSON.parse(json) as WitcPlayStateDto;
  }, []);

  const buildFinishPayload = useCallback((): WitcFinishAudit | null => {
    const wasm = exportsRef.current;
    if (!wasm) return null;
    const json = wasm.buildFinishPayload();
    if (!json || json === '{}') return null;
    return JSON.parse(json) as WitcFinishAudit;
  }, []);

  const disposeSession = useCallback(() => {
    exportsRef.current?.disposeSession();
    exportsRef.current = null;
    setReady(false);
  }, []);

  return {
    ready,
    error,
    submitAnswer,
    getPlayState,
    buildFinishPayload,
    disposeSession,
  };
}
